/**
 * ImageLightbox — full-viewport modal that shows one captured image large.
 * Backdrop + ESC close, mirroring the lib/ui/confirm-dialog overlay pattern.
 */

import { useEffect } from "react";
import { X } from "lucide-react";
import { ImageThumb } from "./ImageThumb";
import type { ImageDatapoint } from "@/lib/api";

export function ImageLightbox({
  image,
  onClose,
}: {
  image: ImageDatapoint;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Image preview"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-on-surface/70 backdrop-blur-sm"
      />
      <div className="relative flex max-h-full w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-surface shadow-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-border p-3">
          <span className="truncate text-xs text-on-surface-muted">
            {image.metric} · {new Date(image.captured_at).toLocaleString()}
            {!image.compressed ? " · full quality" : ""}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close preview"
            className="rounded-md p-1.5 text-on-surface-muted hover:bg-surface-muted hover:text-on-surface"
          >
            <X className="size-4" />
          </button>
        </div>
        <div className="flex items-center justify-center overflow-auto bg-surface-muted p-2">
          <ImageThumb
            imageId={image.image_id}
            alt={image.metric}
            className="max-h-[75vh] w-auto object-contain"
          />
        </div>
      </div>
    </div>
  );
}
